import React, { useEffect, useState, useRef } from 'react';
import { Table, Input } from 'antd';
import Button from 'components/Button/Button';

import axios from 'axios';
import { InstanceWithAuth, USER_BUSINESS } from 'api/routes';
import enUS from 'date-fns/locale/en-US'; // Adjust import path as needed
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { getSessionToken } from 'utils/session';
import * as XLSX from 'xlsx';

import './styles.css'; // Import the CSS file here
import { dotPulse } from 'ldrs';
dotPulse.register();
const Business = () => {
  const [userData, setUserData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pdfLoading, setPDFLoading] = useState({
    loading: false,
    transaction_id: '',
  });

  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const datePickerRef = useRef(null);

  const fetchData = async (startDate, endDate) => {
    setLoading(true);
    try {
      const res = await InstanceWithAuth.get(USER_BUSINESS, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setUserData(res.data.data);
      setStartDate(res.data.start_date);
      setEndDate(res.data.end_date);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [datePickerRef]);

  const downloadPdf = async (
    pdfName,
    isDumped,
    bookingType,
    pdfUrl,
    transactionId
  ) => {
    setPDFLoading({
      loading: true,
      transaction_id: transactionId, // Set the transaction ID
    });

    try {
      if (isDumped && bookingType === 'mis-offline') {
        window.open(pdfUrl, '_blank');
      } else if (!isDumped && bookingType == 'online') {
        // Open the PDF URL directly in a new tab if the conditions match
        window.open(pdfUrl, '_blank');
      } else if (isDumped || bookingType == 'online') {
        const url = `${process.env.REACT_APP_BACKEND_URL}/ieadmin/pdf_link_reports/`;
        const params = {
          pdf_name: pdfName,
          is_dumped: isDumped ? 'YES' : 'NO',
          booking_type: bookingType,
        };

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${getSessionToken()}`,
          },
        });

        window.open(response.data, '_blank');
      } else {
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('Error fetching PDF link report:', error);
    } finally {
      setPDFLoading({
        loading: false,
        transaction_id: '', // Clear transaction ID when done
      });
    }
  };

  const data = userData.map((i, index) => ({
    key: index,
    transaction_id: `EFF|${i.transaction_id}`,
    product_type: `${i.transaction_id__insurance_product_id__parent_product_name} - ${i.transaction_id__insurance_sub_product_id__name}`,
    sub_product: i.transaction_id__insurance_sub_product_id__alias,
    vehicle_number:
      i.transaction_id__transaction_user_input_motor_id__vehicle_registration_no,
    customer_name: i.policy_holder_name,
    policy_number: i.policy_number,
    company_name: i.insurance_company_master_id__name,
    company_url: i.insurance_company_master_id__logo_url,
    premium_amount: i.gross_premium,
    pdf_name: i.pdf_name,
    isDumped: i.transaction_id__is_dumped,
    booking_type: i.transaction_id__booking_type,
    url: i.url,
  }));

  const columns = [
    {
      title: 'Details',
      dataIndex: 'name',
      render: (_, record) => (
        <div className="flex">
          <div className="text-4xl h-14 mt-2 border-2 border-slate-300 rounded-lg pb-3 mr-3 shadow-[0_-2px_4px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.3)] shadow-slate-300">
            {(() => {
              switch (record.sub_product) {
                case 'privatecar':
                  return '🚗';
                case '2w':
                  return '🛵';
                case 'individual_health':
                case 'multi_individual_health':
                  return '🏥';
                case 'family_health':
                  return '👨‍👩‍👧‍👦';
                case 'individual_travel':
                  return '✈️';
                case 'family_travel':
                  return '✈️';
                case 'multi_trip_travel':
                  return '✈️';
                case 'student_travel':
                  return '✈️';
                case 'pcv':
                  return '🚌';
                case 'gcv':
                  return '🚚';
                case 'workman_componsation':
                  return '⚒️';
                case 'fire_burglar':
                  return '🔥';
                case 'marine':
                  return '⛴️';
                case 'home':
                  return '🏠';
                case 'term':
                  return '📜';
                case 'sme':
                  return '🏢';
                case 'gmc':
                  return '🏥';
                case 'gpa':
                  return '👤';
                case 'endowment':
                  return '💰';
                case 'ulip':
                  return '❤️';
                case 'life':
                  return '❤️';
                case 'miscellaneous':
                  return '🦺';
                default:
                  return '❓';
              }
            })()}
          </div>
          <div>
            <div className="text-slate-500 text-sm font-bold">
              {record.customer_name || ''}
            </div>
            <div className="text-slate-500 text-sm font-bold">
              {record.vehicle_number?.toUpperCase()}
            </div>
            <div className="text-slate-500 text-sm font-bold">
              {record.policy_number?.toUpperCase()}
            </div>
          </div>
        </div>
      ),
    },

    {
      title: 'Transaction ID',
      align: 'center',
      dataIndex: 'transaction_id',
    },

    {
      title: 'Insurance Company',
      dataIndex: 'company_name',
      align: 'center',
      render: (_, record) => {
        return (
          <div className="flex items-center justify-center">
            {record.company_url ? (
              <img
                src={record.company_url}
                alt="Insurance Company Logo"
                className="h-8 w-auto" // Adjust height and width as needed
              />
            ) : (
              <span className="text-gray-500">{record.company_name}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Premium Amount',
      align: 'center',
      dataIndex: 'premium_amount',
    },
    {
      title: 'Download PDF',
      align: 'center',
      dataIndex: 'quote_id',
      render: (text, record, index) =>
        record.pdf_name || record.url ? (
          <Button
            className=" px-[6px] py-2"
            variant="default"
            size="small"
            onClick={() =>
              downloadPdf(
                record.pdf_name,
                record.isDumped,
                record.booking_type,
                record.url,
                record.transaction_id
              )
            }
          >
            {pdfLoading.loading &&
            pdfLoading.transaction_id == record.transaction_id ? (
              <>
                Downloading
                <span style={{ marginLeft: '8px' }}>
                  {' '}
                  {/* Adjust margin as needed */}
                  <l-dot-pulse
                    size="25"
                    speed="1.3"
                    color="white"
                  ></l-dot-pulse>
                </span>
              </>
            ) : (
              'Download PDF'
            )}
          </Button>
        ) : (
          ''
        ),
    },
  ];

  function formatDate(str, reverse) {
    var date = new Date(str),
      mnth = ('0' + (date.getMonth() + 1)).slice(-2),
      day = ('0' + date.getDate()).slice(-2);
    if (reverse) return [date.getFullYear(), mnth, day].join('-');
    else return [day, mnth, date.getFullYear()].join('-');
  }

  const handleDateRange = (ranges) => {
    const { selection } = ranges;
    setDateRange([selection]);
    setStartDate(formatDate(selection.startDate, true));
    setEndDate(formatDate(selection.endDate, true));
  };

  const handleOk = async () => {
    setShowDatePicker(false);
    setLoading(true);

    try {
      const res = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_URL
        }/pos/pos_business/?start_date=${startDate}${
          endDate ? `&end_date=${endDate}` : ''
        }`,
        {
          headers: {
            Authorization: `Bearer ${getSessionToken()}`,
          },
        }
      );
      setUserData(res.data.data, 'filtered data');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching filtered data:', err);
      setLoading(false);
    }
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false); // Close the DatePicker dialog on clear
  };
  const processDataForExcel = (data) => {
    // Map through the array and format the data
    return data.map((item) => ({
      'Transaction Id': 'EFF|' + item.transaction_id__id,
      'Policy Number': item.policy_number,
      'Booking Type': item.transaction_id__booking_type,
      'Company Name': item.insurance_company_master_id__name,
      Product: item.transaction_id__insurance_product_id__parent_product_name,
      'Sub Product': item.transaction_id__insurance_sub_product_id__name,
      'Registration Number':
        item.transaction_id__transaction_user_input_motor_id__vehicle_registration_no,
      'Business Type':
        item.transaction_id__transaction_user_input_motor_id__business_type,
      Premium: item.gross_premium,
      'Policy Holder Name': item.policy_holder_name,
    }));
  };

  const handleDownloadExcel = () => {
    // Process the data to get the desired structure
    setLoadingReport(true);
    const formattedData = processDataForExcel(userData);

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Policies');

    // Write the Excel file and trigger the download
    XLSX.writeFile(workbook, 'Policies.xlsx');
    setLoadingReport(false);
  };

  return (
    <div className="pt-4">
      <div className="flex justify-center">
        <div className="w-10/12 flex flex-col gap-4 justify-center py-10 shadow-[0_-2px_4px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.3)]  shadow-slate-300 rounded-lg px-8 mb-4">
          <p className="font-bold text-2xl  text-slate-500">Business Status</p>

          <div className="relative flex justify-between items-center shadow-[0_-2px_4px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.3)]  shadow-slate-300 rounded-lg p-4">
            {/* Left Side: Download Excel Button */}
            <Button
              onClick={handleDownloadExcel}
              variant="success"
              className="px-4 py-2"
            >
              {loadingReport ? (
                <span style={{ marginLeft: '8px' }}>
                  {'Downloading'}
                  {/* Adjust margin as needed */}
                  <l-dot-pulse
                    size="25"
                    speed="1.3"
                    color="white"
                  ></l-dot-pulse>
                </span>
              ) : (
                'Download Excel'
              )}
            </Button>

            {/* Right Side: Date Range Picker */}
            <div>
              {/* <h2 className="text-lg font-semibold text-right">Select Date Range</h2> */}
              <Input
                placeholder="Select Date Range"
                className="w-[30vh]"
                onFocus={() => setShowDatePicker(true)}
                value={startDate && endDate ? `${startDate} - ${endDate}` : ''}
              />
            </div>
          </div>

          {/* Date Picker Modal */}
          {showDatePicker && (
            <div className="modal-overlay">
              <div className="custom-modal" ref={datePickerRef}>
                <div className="modal-header flex justify-between items-center bg-gray-200 p-2 rounded-t-lg"></div>
                <div className="modal-body p-4">
                  <DateRangePicker
                    locale={enUS}
                    ranges={dateRange}
                    onChange={handleDateRange}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                  />
                </div>
                <div className="modal-footer flex justify-end p-2 rounded-b-lg">
                  <button
                    className="mr-2 px-4 py-1 text-blue-300 hover:text-blue-400 scale-105 rounded-lg border-none duration-700 cursor-pointer"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    className="px-4 py-1 bg-blue-300 hover:bg-white hover:text-blue-300 hover:border-none rounded-lg duration-700 cursor-pointer"
                    onClick={handleOk}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Selected Date Range */}
          {startDate && endDate ? (
            <p className="flex justify-center text-bold text-md text-slate-500 font-bold">
              {startDate.split('-').reverse().join('-')} to{' '}
              {endDate.split('-').reverse().join('-')}
            </p>
          ) : (
            ''
          )}

          {/* Table */}
          <Table
            columns={columns}
            dataSource={data}
            scroll={{ x: true }}
            loading={loading}
            rowKey={(record) => record.quote_id}
          />
        </div>
      </div>
    </div>
  );
};

export default Business;
